// Field registry for the transaction detail view.
//
// Mobile renders the equivalent screen (TransactionDetailsBody) as ~1400 lines
// of per-type JSX. Describing the rows as data instead keeps the modal itself
// presentational, makes each type's fields readable at a glance, and avoids
// repeating the nested-ternary shape that ActivityDetailsModal is built from.

import { formatAmount, formatFullTimestamp } from "../formatTime";
import { formatReceiptLabel } from "../giftcardReceiptType";
import { getNetworkLabel } from "../../constants/constants";

export type DetailField = {
  label: string;
  value: string;
  /** Renders with a copy affordance (hashes, IDs, addresses). */
  copyable?: boolean;
  /** Renders as an external link. */
  href?: string;
  /** Monospaced, truncating value — for hashes and addresses. */
  mono?: boolean;
};

const upper = (v?: string) => v?.toUpperCase() ?? "";

const coin = (amount?: number, asset?: string) =>
  amount == null ? "" : `${formatAmount(amount)} ${upper(asset)}`;

const naira = (amount?: number) =>
  amount == null ? "" : `₦${formatAmount(amount)}`;

const usd = (amount?: number) =>
  amount == null ? "" : `$${formatAmount(amount)}`;

/** Drops rows with no value so a missing field never renders as an empty row. */
const compact = (fields: (DetailField | false | null | undefined)[]) =>
  fields.filter((f): f is DetailField => Boolean(f && f.value));

export const getExplorerTxUrl = (
  network?: string,
  hash?: string
): string | undefined => {
  if (!hash) return undefined;
  const net = (network || "").toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => net.includes(n));

  if (has("tron", "trc", "trx")) return `https://tronscan.org/#/transaction/${hash}`;
  if (has("bsc", "binance", "bnb", "bep")) return `https://bscscan.com/tx/${hash}`;
  if (has("polygon", "matic")) return `https://polygonscan.com/tx/${hash}`;
  if (has("arbitrum")) return `https://arbiscan.io/tx/${hash}`;
  if (has("optimism", "optimistic")) return `https://optimistic.etherscan.io/tx/${hash}`;
  if (has("avalanche", "avax")) return `https://snowtrace.io/tx/${hash}`;
  if (has("fantom")) return `https://ftmscan.com/tx/${hash}`;
  if (has("base")) return `https://basescan.org/tx/${hash}`;
  if (has("linea")) return `https://lineascan.build/tx/${hash}`;
  if (has("zksync", "zk-sync")) return `https://explorer.zksync.io/tx/${hash}`;
  if (has("eth")) return `https://etherscan.io/tx/${hash}`;
  if (has("sol")) return `https://solscan.io/tx/${hash}`;
  if (has("doge")) return `https://dogechain.info/tx/${hash}`;
  if (has("btc", "bitcoin")) return `https://www.blockchain.com/btc/tx/${hash}`;
  return undefined;
};

const utilityFields = (tx: any): DetailField[] => {
  const data = tx.utility?.data ?? {};
  const meta = data.metaData ?? {};
  return compact([
    { label: "Utility type", value: upper(tx.utility?.type) },
    { label: "Amount purchased", value: naira(Number(data.amount)) },
    { label: "Phone number", value: data.phone ?? data.receiver?.number ?? "" },
    { label: "Meter number", value: data.meter_number ?? "" },
    { label: "Meter name", value: meta.name ?? "" },
    { label: "Meter address", value: meta.address ?? "" },
    { label: "Token", value: data.token ?? data.utilityToken ?? "", copyable: true, mono: true },
    { label: "Units", value: meta.units ? `${meta.units} kWh` : "" },
    { label: "Vend type", value: data.vendType ?? "" },
    { label: "Service provider", value: upper(meta.disco ?? data.network ?? data.provider) },
    { label: "Receipt no.", value: meta.receiptNo ? String(meta.receiptNo) : "" },
  ]);
};

const giftCardFields = (tx: any): DetailField[] => {
  const values: DetailField[] = (tx.tradeValue ?? []).map(
    (entry: any, i: number) => ({
      label: `Card ${i + 1}`,
      value: [
        usd(entry.amount),
        entry.range?.min != null && entry.range?.max != null
          ? `(range $${entry.range.min}–$${entry.range.max})`
          : "",
      ]
        .filter(Boolean)
        .join(" "),
    })
  );

  return compact([
    { label: "Card", value: upper(tx.card?.name) },
    { label: "Receipt type", value: formatReceiptLabel(tx.type) },
    // The backend currently echoes the card name into `country` on some
    // records, so only show it when it actually differs.
    tx.country && tx.country !== tx.card?.name
      ? { label: "Country", value: tx.country }
      : null,
    { label: "Cards submitted", value: tx.tradeValue?.length ? String(tx.tradeValue.length) : "" },
    ...values,
    { label: "Images uploaded", value: tx.images?.length ? String(tx.images.length) : "" },
  ]);
};

/**
 * Rows for one transaction, keyed off the stamped legacy `transactionType`
 * (the same field ActivityDetailsModal branches on).
 */
export const getDetailFields = (tx: any): DetailField[] => {
  const type = tx?.transactionType;
  const explorer = getExplorerTxUrl(tx?.network, tx?.provider?.data?.hash);

  const common = compact([
    { label: "Timestamp", value: tx?.createdAt ? formatFullTimestamp(tx.createdAt) : "" },
    { label: "Transaction ID", value: tx?._id ?? "", copyable: true, mono: true },
  ]);

  const chain = compact([
    { label: "Network", value: tx?.network ? getNetworkLabel(tx.network) : "" },
    { label: "Fee", value: tx?.fees != null ? coin(tx.fees, tx.currency) : "" },
    {
      label: "Transaction hash",
      value: tx?.provider?.data?.hash ?? tx?.txid ?? "",
      copyable: true,
      mono: true,
      href: explorer,
    },
    { label: "To address", value: tx?.toAddress ?? "", copyable: true, mono: true },
    { label: "Quote ID", value: tx?.quoteID ?? "", copyable: true, mono: true },
  ]);

  switch (type) {
    case "deex-crypto-sells":
      return [
        ...compact([
          { label: "Amount sold", value: coin(tx.amount, tx.sellAsset ?? tx.asset) },
          { label: "Amount in NGN", value: naira(tx.amountNGN) },
          { label: "Amount in USD", value: usd(tx.amountUSDT) },
        ]),
        ...common,
      ];

    case "deex-asset-swaps":
      return [
        ...compact([
          { label: "Amount from", value: coin(tx.from?.amount, tx.from?.asset) },
          { label: "Amount to", value: coin(tx.to?.amount, tx.to?.asset) },
        ]),
        ...common,
      ];

    case "deex-deposits":
      return [
        ...compact([{ label: "Amount received", value: coin(tx.amount, tx.currency) }]),
        ...chain,
        ...common,
      ];

    case "deex-crypto-withdrawals":
      // Bill payments share this collection — they're told apart by `utility`.
      if (tx.utility) {
        return [
          ...compact([
            { label: "Amount", value: coin(tx.amount, tx.currency) },
            { label: "Amount in USD", value: usd(tx.usdtAmount) },
          ]),
          ...utilityFields(tx),
          ...common,
        ];
      }
      return [
        ...compact([
          { label: "Amount", value: coin(tx.amount, tx.currency) },
          { label: "Amount in USD", value: usd(tx.usdtAmount) },
          {
            label: "Sent to",
            value: tx.toDeexUser?.username ?? tx.toDeexUser?.email ?? "",
          },
        ]),
        ...chain,
        ...common,
      ];

    case "payouts":
      return [
        ...compact([
          { label: "Amount", value: naira(tx.payoutAmount) },
          { label: "Amount in USD", value: usd(tx.asset?.amountInUSD) },
          { label: "Asset", value: coin(tx.asset?.amount, tx.asset?.name) },
          { label: "Bank", value: tx.recipient?.bankName ?? "" },
          { label: "Account number", value: tx.recipient?.accountNumber ?? "", copyable: true },
          { label: "Account name", value: tx.recipient?.accountName ?? "" },
        ]),
        ...common,
      ];

    case "gift-card-transactions":
      return [...giftCardFields(tx), ...common];

    default:
      return [
        ...compact([
          { label: "Amount", value: coin(tx?.amount, tx?.currency) },
        ]),
        ...common,
      ];
  }
};
